import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import AppContext from "../context/context";
import { loginService } from "../../utils/services/loginService.js";
import { useNavigate } from "react-router-dom";
import { removeStorageItem, setStorageItem } from "../../utils/storage";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

function InputField({
  icon,
  placeholder,
  onChange,
  type,
  value,
  endAdornment,
}) {
  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: "100%",
      }}
    >
      {icon && (
        <span
          style={{
            position: "absolute",
            left: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
          }}
        >
          {icon}
        </span>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: "100%",
          padding: `10px ${endAdornment ? "40px" : "10px"} 10px ${
            icon ? "40px" : "10px"
          }`,
          border: "1px solid white",
          borderRadius: "4px",
          backgroundColor: "transparent",
          color: "white",
          outline: "none",
          caretColor: "white",
          WebkitTextFillColor: "white",
          WebkitBoxShadow: "0 0 0 1000px transparent inset",
        }}
      />
      {endAdornment && (
        <div
          type="button"
          style={{
            position: "absolute",
            right: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "white",
          }}
        >
          {endAdornment}
        </div>
      )}
    </div>
  );
}

function LoginForm({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [pendingResponse, setPendingResponse] = React.useState(null);
  const [clients, setClients] = React.useState([]);
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [step, setStep] = React.useState("credentials");

  const completeLogin = (response, clientId) => {
    setIsAuthenticated(true);
    setStorageItem("token", response.accessToken);
    if (clientId) {
      localStorage.setItem("client_id", clientId);
      loginService
        .login_oms(response)
        .then((res) => {
          localStorage.setItem("rl_oms_token", res?.token);
          localStorage.setItem("rl_oms_user", JSON.stringify(res?.user));
        })
        .catch((err) => {
          console.error(err);
        });
    }
    navigate("/");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      if (!email || !password) {
        toast.error("Please enter email and password", { autoClose: 3000 });
        return;
      }
      const response = await loginService.login(email, password);

      if (response.accessToken) {
        setStorageItem("user", JSON.stringify(response.user));
        setStorageItem("base_token", response.accessToken);
        localStorage.setItem(
          "roles",
          JSON.stringify(response.authentication?.payload?.roles),
        );
        if (
          Array.isArray(response?.authentication?.payload?.clients) &&
          response?.authentication?.payload?.clients?.length > 0
        ) {
          localStorage.setItem(
            "clients",
            JSON.stringify(response.authentication.payload.clients),
          );
          setPendingResponse(response);
          setClients(response.authentication.payload.clients);
          setSelectedClient(response.authentication.payload.clients[0]);
          setStep("select-client");
        } else {
          removeStorageItem("clients");
          completeLogin(
            response,
            response.authentication?.payload?.client || null,
          );
        }
      }
    } catch (error) {
      console.error("Login failed:", error);
      if (error?.response?.status === 401) {
        toast.error("Login failed. Please check your credentials.");
      }
    }
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    if (!selectedClient) {
      toast.error("Please select a client to continue.");
      return;
    }
    try {
      const response = await loginService.loginWithClient(
        pendingResponse.accessToken,
        selectedClient._id,
      );
      completeLogin(response, selectedClient._id);
    } catch (error) {
      console.error("Client login failed:", error);
      toast.error(
        "Failed to authenticate with selected client. Please try again.",
      );
    }
  };

  if (step === "select-client") {
    return (
      <form
        style={{
          position: "relative",
          flexDirection: "column",
          maxWidth: "100%",
          width: "350px",
        }}
      >
        <Box mb={3}>
          <Typography
            fontFamily="poppins"
            fontSize={20}
            fontWeight={700}
            color="white"
            mb={0.5}
          >
            Select Client
          </Typography>
          <Typography
            fontFamily="poppins"
            fontSize={13}
            color="rgba(255,255,255,0.6)"
          >
            Choose the client account you want to log in as.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            maxHeight: 280,
            overflowY: "auto",
            mb: 3,
            pr: 0.5,
          }}
        >
          {clients.map((client) => {
            const isSelected = selectedClient?._id === client._id;
            return (
              <div
                key={client._id}
                onClick={() => setSelectedClient(client)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 14px",
                  border: isSelected
                    ? "1.5px solid #FEC50F"
                    : "1px solid rgba(255,255,255,0.3)",
                  borderRadius: 6,
                  cursor: "pointer",
                  backgroundColor: isSelected
                    ? "rgba(254,197,15,0.08)"
                    : "transparent",
                  transition: "all 0.15s ease",
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    border: isSelected
                      ? "5px solid #FEC50F"
                      : "2px solid rgba(255,255,255,0.5)",
                    flexShrink: 0,
                    transition: "all 0.15s ease",
                  }}
                />
                <div style={{ overflow: "hidden" }}>
                  <Typography
                    fontFamily="poppins"
                    fontSize={14}
                    fontWeight={600}
                    color="white"
                    noWrap
                  >
                    {client.business_name || client.name}
                  </Typography>
                  {client.email && (
                    <Typography
                      fontFamily="poppins"
                      fontSize={12}
                      color="rgba(255,255,255,0.55)"
                      noWrap
                    >
                      {client.email}
                    </Typography>
                  )}
                </div>
              </div>
            );
          })}
        </Box>

        <Button
          onClick={handleClientSubmit}
          type="submit"
          style={{
            backgroundColor: "#FEC50F",
            color: "#000",
            textTransform: "none",
            fontFamily: "poppins",
            fontSize: 16,
            fontWeight: 700,
            width: "100%",
            marginBottom: 12,
          }}
        >
          Continue
        </Button>

        <Button
          onClick={() => {
            setStep("credentials");
            setPendingResponse(null);
            setClients([]);
            setSelectedClient(null);
            removeStorageItem("user");
            removeStorageItem("roles");
            removeStorageItem("base_token");
            removeStorageItem("clients");
          }}
          style={{
            color: "rgba(255,255,255,0.6)",
            textTransform: "none",
            fontFamily: "poppins",
            fontSize: 13,
            width: "100%",
          }}
        >
          ← Back to Login
        </Button>
      </form>
    );
  }

  return (
    <form
      style={{
        position: "relative",
        flexDirection: "column",
        maxWidth: "100%",
        width: "350px",
      }}
    >
      <Box mb={3}>
        <Typography
          variant="body1"
          fontFamily="poppins"
          fontSize={14}
          fontWeight={600}
          color="white"
          textAlign={"left"}
          mb={1}
        >
          Email ID<span style={{ color: "red" }}>*</span>
        </Typography>
        <InputField
          onChange={setEmail}
          type="text"
          icon={<PersonOutlineIcon style={{ color: "white" }} />}
          placeholder="Enter Email ID"
          value={email}
          fullWidth
          style={{
            backgroundColor: "#fff",
            color: "#000",
            marginTop: 8,
            padding: "10px",
            borderRadius: "5px",
          }}
        />
      </Box>

      <Box mb={3}>
        <Typography
          variant="body1"
          fontFamily="poppins"
          fontSize={14}
          fontWeight={600}
          color="white"
          textAlign={"left"}
          mb={1}
        >
          Password<span style={{ color: "red" }}>*</span>
        </Typography>
        <InputField
          onChange={setPassword}
          type={showPassword ? "text" : "password"}
          icon={<LockOutlinedIcon style={{ color: "white" }} />}
          placeholder="Enter Password"
          value={password}
          fullWidth
          endAdornment={
            <IconButton
              style={{ color: "white" }}
              onClick={() => setShowPassword(!showPassword)}
              aria-label="Toggle password visibility"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          }
          style={{
            backgroundColor: "#fff",
            color: "#000",
            marginTop: 8,
            padding: "10px",
            borderRadius: "5px",
          }}
        />
      </Box>

      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        mb={4}
        fontFamily="poppins"
      >
        <Link
          to="/forgot-password"
          style={{
            textDecoration: "none",
            color: "#FFF",
            fontFamily: "poppins",
            fontSize: 14,
          }}
        >
          Forgot Password?
        </Link>
      </Box>

      <Button
        onClick={handleLogin}
        type="submit"
        style={{
          backgroundColor: "#FEC50F",
          color: "#000",
          textTransform: "none",
          fontFamily: "poppins",
          fontSize: 16,
          fontWeight: 700,
          width: "100%",
        }}
      >
        Login
      </Button>

      <Typography
        marginTop={3}
        fontSize={14}
        fontFamily="poppins"
        fontWeight={500}
        textAlign="center"
        color={"#fff"}
      >
        New to Dropskip?{" "}
        <a
          href="https://www.redefinedlogistics.com/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "underline", color: "#fff" }}
        >
          Learn more.
        </a>
      </Typography>
    </form>
  );
}

const Login = () => {
  const { isAuthenticated, setIsAuthenticated } = React.useContext(AppContext);
  const navigate = useNavigate();
  const { state } = useLocation();
  const [loading, setLoading] = React.useState(true); //
  // const fetchClientId = async (email) => {
  //   const clientDetails = await clientService.fetch(15, 0, '', email)
  //   localStorage.setItem('client_id', clientDetails[0]?.id)
  // }
  const isMobile = useMediaQuery("(max-width:750px)");
  React.useEffect(() => {
    if (isAuthenticated) {
      const path = localStorage.getItem("path");
      navigate(path && path !== "undefined" ? path : "/");
    }
    setLoading(false);
  }, []);
  React.useEffect(() => {
    if (state?._id) {
      toast.success("Account Created Successfully, Please Login", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });
    }
  }, [state?._id]);
  if (loading) {
    return (
      <main
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#151D48",
        }}
      >
        <Typography color="white" variant="h5">
          Loading...
        </Typography>
      </main>
    );
  }
  return (
    <main
      style={{ backgroundColor: "#fff", display: "flex", flexDirection: "row" }}
    >
      {!isMobile ? (
        <div
          style={{
            backgroundColor: "#fff",
            display: "flex",
            flex: 1,
            height: "100vh",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              marginTop: 20,
              marginLeft: 20,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
            }}
          >
            <img
              src="/DropSkipLogo.png"
              alt="Company Logo"
              style={{
                marginBottom: 10,
                marginLeft: 20,
                aspectRatio: "2.0",
                width: "137px",
              }}
            />
            <Typography
              fontFamily={"poppins"}
              fontSize={"16px"}
              fontWeight={600}
              color={"black"}
              style={{ textAlign: "center" }}
            >
              Powered by Redefined Logistics
            </Typography>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
            }}
          >
            <img src="/mobile.png" alt="Company Logo" />
          </div>
        </div>
      ) : (
        <></>
      )}

      <div
        style={{
          display: "flex",
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#151D48",
          height: "100vh",
          position: "relative",
        }}
      >
        {isMobile && (
          <div style={{ position: "absolute", top: 20, left: 20 }}>
            <img
              src="./DropSkipLogo.png"
              alt="Dropskip Logo"
              style={{ aspectRatio: "2.0", width: "100px" }}
            />
            <Typography
              fontFamily={"poppins"}
              fontSize={"10px"}
              fontWeight={500}
              color={"#fff"}
              style={{ marginTop: 5, textAlign: "left" }}
            >
              Powered by Redefined Logistics
            </Typography>
          </div>
        )}

        <Button
          variant="outlined"
          style={{
            backgroundColor: "#FEC50F",
            color: "#000",
            textTransform: "none",
            position: "absolute",
            right: 20,
            top: 25,
            fontFamily: "poppins",
            fontSize: 14,
            fontWeight: 500,
          }}
          onClick={() =>
            window.open(
              "https://www.redefinedlogistics.com/contactus",
              "_blank",
            )
          }
        >
          Contact Us
        </Button>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <LoginForm setIsAuthenticated={setIsAuthenticated} />
        </div>
      </div>
    </main>
  );
};

export default Login;
